import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Plus, Users, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const StaffDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [myStudents, setMyStudents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', dob: '', email: '', contact_number: '',
    college_name: '', course_name: '', intake: '', company_consultant: '',
    current_stage: 'Applied', is_converted: false, converted_by_staff: '',
    notes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    setUser(userData);
    setFormData(prev => ({ ...prev, converted_by_staff: userData.full_name }));
    fetchMyStudents();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchMyStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`, getAuthHeader());
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      // Filter to show only students created by this staff member
      const filtered = response.data.filter(s => s.created_by === userData.user_id);
      setMyStudents(filtered);
    } catch (error) {
      toast.error('Failed to load students');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/students`, formData, getAuthHeader());
      toast.success('Student added successfully');
      setShowForm(false);
      setFormData({
        name: '', dob: '', email: '', contact_number: '',
        college_name: '', course_name: '', intake: '', company_consultant: '',
        current_stage: 'Applied', is_converted: false, converted_by_staff: user.full_name,
        notes: ''
      });
      fetchMyStudents();
    } catch (error) {
      toast.error('Failed to add student');
    }
  };

  const stages = ['Applied', 'Documents Submitted', 'Interview Scheduled', 'Admitted', 'Enrolled', 'Rejected'];
  const convertedCount = myStudents.filter(s => s.is_converted).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary via-slate-800 to-primary shadow-2xl border-b-4 border-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <img
                src="https://customer-assets.emergentagent.com/job_canada-pathway-3/artifacts/wzgtavxp_IMG_0276-removebg-preview.png"
                alt="ELARA"
                className="h-14 w-auto"
              />
              <div className="border-l-2 border-secondary pl-6">
                <h1 className="text-3xl font-playfair font-bold text-white tracking-wide">Elara Team</h1>
                <p className="text-sm text-secondary mt-1">Welcome, <span className="font-semibold">{user?.full_name}</span></p>
              </div>
            </div>
            <button 
              onClick={handleLogout} 
              className="flex items-center gap-3 px-6 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 transition-all duration-300 group"
            >
              <LogOut size={18} className="group-hover:rotate-180 transition-transform duration-500" /> 
              <span className="text-white font-semibold">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-white to-slate-50 p-6 shadow-xl border-l-4 border-primary hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">My Applications</p>
                <p className="text-4xl font-playfair font-bold text-primary">{myStudents.length}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <Users className="text-primary" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-white to-green-50 p-6 shadow-xl border-l-4 border-green-600 hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Converted</p>
                <p className="text-4xl font-playfair font-bold text-green-600">{convertedCount}</p>
              </div>
              <div className="p-3 bg-green-600/10 rounded-full">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-white to-amber-50 p-6 shadow-xl border-l-4 border-secondary hover:shadow-2xl transition-all duration-300"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Success Rate</p>
                <p className="text-4xl font-playfair font-bold text-secondary">
                  {myStudents.length > 0 ? Math.round((convertedCount / myStudents.length) * 100) : 0}%
                </p>
              </div>
              <div className="p-3 bg-secondary/10 rounded-full">
                <CheckCircle className="text-secondary" size={24} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Add Button */}
        <div className="mb-8">
          <button
            onClick={() => setShowForm(true)}
            className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-secondary to-secondary-light text-primary font-bold text-lg hover:from-secondary-light hover:to-secondary transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
          >
            <Plus size={24} /> Add New Student
          </button>
        </div>

        {/* Students List */}
        <div className="bg-white shadow-2xl border-t-4 border-secondary">
          <div className="px-6 py-4 bg-slate-50 border-b-2 border-slate-200">
            <h2 className="text-xl font-playfair font-bold text-primary">My Students ({myStudents.length})</h2>
          </div>
          <div className="divide-y divide-slate-200">
            {myStudents.map((student) => (
              <div key={student.student_id} className="p-6 hover:bg-slate-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900">{student.name}</h3>
                    <p className="text-sm text-slate-600 mt-1">{student.email} • {student.contact_number}</p>
                    <div className="flex gap-4 mt-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold">{student.college_name}</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold">{student.course_name}</span>
                      <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold">{student.intake}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold">{student.current_stage}</span>
                    <div className="mt-2">
                      {student.is_converted ? (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold">CONVERTED</span>
                      ) : (
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold">IN PROGRESS</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {myStudents.length === 0 && (
              <div className="text-center py-16">
                <Users className="mx-auto text-slate-300 mb-4" size={64} />
                <p className="text-xl font-semibold text-slate-400">No students yet</p>
                <p className="text-sm text-slate-400 mt-2">Click "Add New Student" to get started</p>
              </div>
            )}
          </div>
        </div>

        {/* Add Student Form - Same luxury form as admin */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white w-full max-w-4xl my-8 shadow-2xl border border-slate-200"
            >
              <div className="bg-gradient-to-r from-primary via-primary-light to-primary px-8 py-6 border-b-2 border-secondary">
                <h2 className="text-3xl font-playfair font-bold text-white">Add New Student</h2>
                <p className="text-slate-200 mt-1">Fill in the student information below</p>
              </div>

              <form onSubmit={handleSubmit} className="p-8">
                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Student Name *</label>
                      <input type="text" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Date of Birth *</label>
                      <input type="date" value={formData.dob} onChange={(e) => setFormData({...formData, dob: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address *</label>
                      <input type="email" placeholder="student@example.com" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Contact Number *</label>
                      <input type="tel" placeholder="+1 (XXX) XXX-XXXX" value={formData.contact_number} onChange={(e) => setFormData({...formData, contact_number: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium" />
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">Academic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">College Name *</label>
                      <input type="text" placeholder="College Name" value={formData.college_name} onChange={(e) => setFormData({...formData, college_name: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Course Name *</label>
                      <input type="text" placeholder="Course/Program Name" value={formData.course_name} onChange={(e) => setFormData({...formData, course_name: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Intake *</label>
                      <select value={formData.intake} onChange={(e) => setFormData({...formData, intake: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium">
                        <option value="">Select Intake</option>
                        <option value="Spring 2025">Spring 2025</option>
                        <option value="Fall 2025">Fall 2025</option>
                        <option value="Winter 2026">Winter 2026</option>
                        <option value="Spring 2026">Spring 2026</option>
                        <option value="Fall 2026">Fall 2026</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Company/Consultant *</label>
                      <input type="text" placeholder="Consultant Name" value={formData.company_consultant} onChange={(e) => setFormData({...formData, company_consultant: e.target.value})} required className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium" />
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">Application Status</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Current Stage *</label>
                      <select value={formData.current_stage} onChange={(e) => setFormData({...formData, current_stage: e.target.value})} className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium">
                        {stages.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-3 px-4 py-3 border-2 border-slate-300 cursor-pointer hover:border-secondary transition-colors">
                        <input type="checkbox" checked={formData.is_converted} onChange={(e) => setFormData({...formData, is_converted: e.target.checked})} className="w-5 h-5 accent-secondary" />
                        <span className="font-semibold text-slate-700">Student Converted</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-xl font-playfair font-semibold text-primary mb-4 pb-2 border-b-2 border-secondary/30">Additional Notes</h3>
                  <textarea placeholder="Add any additional notes or comments..." value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={4} className="w-full px-4 py-3 border-2 border-slate-300 focus:outline-none focus:border-secondary transition-colors font-medium resize-none" />
                </div>

                <div className="flex gap-4 pt-6 border-t-2 border-slate-200">
                  <button type="submit" className="flex-1 px-8 py-4 bg-gradient-to-r from-secondary to-secondary-light text-primary font-bold text-lg hover:from-secondary-light hover:to-secondary transition-all duration-300 shadow-lg">
                    Save Student
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="px-8 py-4 border-2 border-slate-400 text-slate-700 font-semibold hover:bg-slate-100 transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};
